import { useTheme } from '@material-ui/core';
import { UserNotificationPreferencesService } from 'api-clients/monolith';
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import uniqBy from 'lodash/uniqBy';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import Select from 'react-select';

import messages from './messages';
import { StageOptionType, StageType } from './types';

// One menu height (react-select's maxMenuHeight) ahead, so a held arrow key
// does not wrap to the top before the next page arrives.
const LOAD_MORE_THRESHOLD_PX = 300;
const PER_PAGE = 50;
const SEARCH_DEBOUNCE_MS = 300;

export interface SelectStageProps {
  notifiableType: string;
  notifiableId: string;
  stageType: StageType;
  selectedStages: StageOptionType[];
  onChange: (selectedStages: StageOptionType[]) => void;
}

export const SelectStage: React.FC<SelectStageProps> = ({
  notifiableType,
  notifiableId,
  stageType,
  selectedStages,
  onChange,
}) => {
  const intl = useIntl();
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadedQuery, setLoadedQuery] = useState<string>('');
  const [nextPage, setNextPage] = useState<number | null>(1);
  const [searchInput, setSearchInput] = useState<string>('');
  const [stageOptions, setStageOptions] = useState<StageOptionType[]>([]);
  const latestRequestId = useRef(0);

  const loadStages = useCallback(
    (query: string, page: number) => {
      latestRequestId.current += 1;
      const requestId = latestRequestId.current;
      const isLatestRequest = () => requestId === latestRequestId.current;

      setIsLoading(true);
      UserNotificationPreferencesService.getInternalApiNotificationPreferencesStages(
        notifiableType,
        notifiableId,
        stageType,
        query,
        page,
        PER_PAGE,
      )
        .then(result => {
          if (!isLatestRequest()) return;

          setStageOptions(previous =>
            page === 1
              ? result.stages
              : uniqBy([...previous, ...result.stages], 'value'),
          );
          setLoadedQuery(query);
          setNextPage(Number(result.pagination.next_page) || null);
          setIsLoading(false);
        })
        .catch(() => {
          if (!isLatestRequest()) return;

          // Keeps the previous query's stages from showing under the new input.
          if (page === 1) setStageOptions([]);
          setIsLoading(false);
        });
    },
    [notifiableId, notifiableType, stageType],
  );

  const searchStages = useMemo(
    () => debounce((query: string) => loadStages(query, 1), SEARCH_DEBOUNCE_MS),
    [loadStages],
  );

  useEffect(() => {
    const currentRequestId = latestRequestId;
    return () => {
      searchStages.cancel();
      currentRequestId.current += 1;
    };
  }, [searchStages]);

  const onMenuOpen = () => {
    if (isEmpty(stageOptions) && !isLoading) loadStages(searchInput, 1);
  };

  // Native scroll covers wheel, trackpad, scrollbar and arrow keys, which
  // react-select's onMenuScrollToBottom (wheel and touch only) misses.
  const onMenuScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const { clientHeight, scrollHeight, scrollTop } =
      event.target as HTMLElement;
    const isNearBottom =
      scrollHeight - scrollTop - clientHeight <= LOAD_MORE_THRESHOLD_PX;

    if (isNearBottom && nextPage && !isLoading)
      loadStages(loadedQuery, nextPage);
  };

  return (
    // The menu renders in a portal; React still delivers its scroll events here.
    <div onScrollCapture={onMenuScroll}>
      <Select
        name={`CustomStagesSelect-${notifiableType}-${notifiableId}`}
        styles={{
          multiValue: provided => ({
            ...provided,
            '&:hover': {
              backgroundColor: theme.palette.common.brandTintHover,
            },
            alignItems: 'center',
            backgroundColor: theme.palette.common.brandTint,
            borderRadius: theme.highlightBorderRadius,
            display: 'flex',
            gap: theme.spacing(1),
            margin: theme.spacing(0.25),
            marginLeft: 'unset',
            padding: theme.spacing(1, 1.75, 1, 1.25),
          }),
          menuPortal: base => ({ ...base, zIndex: 9999 }),
        }}
        isMulti
        canSelectAll={false}
        menuPortalTarget={document.body}
        options={stageOptions}
        onChange={options =>
          isEmpty(options)
            ? onChange([])
            : // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
              Array.isArray(options) && onChange(options)
        }
        placeholder={intl.formatMessage(messages.selectPlaceolderText)}
        value={selectedStages}
        closeMenuOnSelect={false}
        inputValue={searchInput}
        onInputChange={value => {
          if (value === searchInput) return;

          setSearchInput(value);
          searchStages(value);
        }}
        onMenuOpen={onMenuOpen}
        hideSelectedOptions={false}
        isClearable
        isLoading={isLoading}
        noOptionsMessage={() => (isLoading ? 'Loading ...' : 'No Result')}
        filterOption={() => true}
      />
    </div>
  );
};
